import {
  getShoppingCart,
  addCartItem,
  deleteCartItem,
  updateCartItem,
  updateShoppingCart,
  getCartFromSession,
  updateCart,
} from "../../services/cartShoppingService";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CartProduct = {
  productCode: string;
  quantity: number;
};

interface ProductState {
  shoppingCart: CartProduct[];
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  shoppingCart: getCartFromSession(),
  loading: false,
  error: null,
};

const cartShoppingSlice = createSlice({
  name: "shoppingCart",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getShoppingCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getShoppingCart.fulfilled,
        (state, action: PayloadAction<CartProduct[]>) => {
          state.loading = false;
          state.shoppingCart = action.payload;
        }
      )
      .addCase(getShoppingCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCartItem.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(
        addCartItem.fulfilled,
        (state, action: PayloadAction<CartProduct>) => {
          state.loading = false;
          const itemIdx = state.shoppingCart.findIndex(
            (item) => item.productCode === action.payload.productCode
          );

          if (itemIdx === -1) {
            state.shoppingCart.push(action.payload);
          } else {
            state.shoppingCart[itemIdx].quantity += action.payload.quantity;
          }

          sessionStorage.setItem(
            "shopping_cart",
            JSON.stringify(state.shoppingCart)
          );
        }
      )

      .addCase(updateShoppingCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateShoppingCart.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      .addCase(
        updateShoppingCart.fulfilled,
        (state, action: PayloadAction<CartProduct[]>) => {
          state.loading = false;
          state.shoppingCart = action.payload;
        }
      )

      .addCase(deleteCartItem.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteCartItem.fulfilled,
        (state, action: PayloadAction<{ code: string; remove: boolean }>) => {
          state.loading = false;
          const itemIdx = state.shoppingCart.findIndex(
            (item) => item.productCode === action.payload.code
          );

          if (itemIdx > -1) {
            const newQuantity = state.shoppingCart[itemIdx].quantity - 1;
            if (action.payload.remove || newQuantity === 0) {
              state.shoppingCart = state.shoppingCart.filter(
                (item) => item.productCode !== action.payload.code
              );
            } else {
              state.shoppingCart[itemIdx].quantity -= 1;
            }
          }

          sessionStorage.setItem(
            "shopping_cart",
            JSON.stringify(state.shoppingCart)
          );
        }
      )

      .addCase(updateCartItem.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateCartItem.fulfilled,
        (state, action: PayloadAction<CartProduct>) => {
          state.loading = false;
          const itemIdx = state.shoppingCart.findIndex(
            (item) => item.productCode === action.payload.productCode
          );
          if (itemIdx > -1) {
            state.shoppingCart = state.shoppingCart.map((item, idx) =>
              idx === itemIdx ? action.payload : item
            );
          }
        }
      )

      // Update shopping cart user
      .addCase(updateCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCart.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default cartShoppingSlice.reducer;
